import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import { useRef, useState } from 'react';
import { useQuery } from 'react-query';

import Block from 'shopper/components/Block';
import Button from 'shopper/components/Button';
import Icon from 'shopper/components/Icon';
import Tag from 'shopper/components/Tag';

import { sendEvent } from 'lib/gtag';
import { notificationQueryKeys } from 'lib/queryKeys';
import { get } from 'lib/request';

import { useUser } from 'providers/UserProvider';

import Z_INDEX_SCALE from 'constants/zIndexScale';

const NotificationDropdown = dynamic(() => import('./NotificationDropdown'), {
  ssr: false,
});

const NotificationButton = ({ gaEventCategory }) => {
  const notificationsButtonRef = useRef(null);
  const [isNotificationsOpen, setNotificationsOpen] = useState(false);
  const { isLoggedIn } = useUser();

  const { data: notificationCounters, isSuccess } = useQuery(
    notificationQueryKeys.counts(),
    {
      queryFn: () => get('/notification/count/types'),
      enabled: isLoggedIn,
      refetchOnWindowFocus: true,
    }
  );

  const onNotificationButtonClick = () => {
    if (!isLoggedIn) {
      sendEvent({
        action: 'notifications_unlogged_open',
        category: gaEventCategory,
      });
    }

    sendEvent({
      action: 'notification_list',
      category: gaEventCategory,
    });
    setNotificationsOpen((prev) => !prev);
  };

  const onNotificationDropdownClose = () => {
    if (!isLoggedIn) {
      sendEvent({
        action: 'notifications_unlogged_close',
        category: gaEventCategory,
      });
    }

    setNotificationsOpen(false);
  };

  return (
    <Block position="relative" width="max-content" ml={2}>
      <Block position="relative">
        <Button
          size="size2"
          type="subtle"
          position="relative"
          aria-label="Abrir notificações"
          data-test-selector="open-notification-dropdown"
          ref={notificationsButtonRef}
          zIndex={isNotificationsOpen ? Z_INDEX_SCALE.SIZE_6 : null}
          onClick={onNotificationButtonClick}
        >
          <Icon name="notification-filled" size="size1" />
          {isSuccess && notificationCounters.countAll > 0 && (
            <Tag
              size="size4"
              position="absolute"
              type="alert"
              mt={1}
              mr={1}
              px={1}
              top={-2}
              right={-1}
            >
              <b>{notificationCounters.countAll}</b>
            </Tag>
          )}
          {!isLoggedIn && (
            <Tag
              size="size4"
              position="absolute"
              type="alert"
              p="6px"
              mt="6px"
              mr="6px"
              top={0}
              right={0}
            />
          )}
        </Button>
      </Block>
      {isNotificationsOpen && (
        <NotificationDropdown
          isOpen={isNotificationsOpen}
          openerRef={notificationsButtonRef}
          onClose={onNotificationDropdownClose}
        />
      )}
    </Block>
  );
};

NotificationButton.propTypes = {
  gaEventCategory: PropTypes.string.isRequired,
};

export default NotificationButton;
